import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { AuthenticatedHttpService } from './authenticatedhttp.service';

import { OptionsAdmin } from '../model/options-admin';
import { OptionsAdminSearch } from '../model/options-admin-search';
import { Option } from '../model/option';
import { AdminOption } from '../model/admin-option';
import { AdminOptionCategory } from '../model/admin-option-category';
import { Category } from '../model/category';
import { OptionsAdminAsidePage } from '../model/pageloadoptions/options-admin-aside-options';

import * as _ from 'lodash';

@Injectable()
export class OptionsAdminService {
    private baseUrl;
    private _optionsAdminCache: OptionsAdmin;
    //private _optionsAdminCatsCache: AdminOptionCategory[];
    //private _currentUser: CurrentUser;

    showAdminAside$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    showBulkCategoryChangeAside$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    option$: BehaviorSubject<Option> = new BehaviorSubject(new Option());
    allCustomerCategories$: BehaviorSubject<AdminOptionCategory[]> = new BehaviorSubject(new Array<AdminOptionCategory>());
    userId$: BehaviorSubject<string> = new BehaviorSubject(null);
    plantId$: BehaviorSubject<number> = new BehaviorSubject(null);
    optionsadminasidePageOptions$: BehaviorSubject<OptionsAdminAsidePage> = new BehaviorSubject(null);
    changedAdminOption$: BehaviorSubject<Option> = new BehaviorSubject(null);
    reloadOptionsAdmin$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    uncheckBulkChangeSelectedOptions$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    bulkchangeOptions$: BehaviorSubject<Option[]> = new BehaviorSubject(null);
    selectedProductLineCode$: BehaviorSubject<string> = new BehaviorSubject(null);

    constructor(
        private _http: AuthenticatedHttpService
	) {
        this.baseUrl = _http.getBaseURLFromCookie();

        //this.currentUserService.currentUser$.subscribe(currentUser => { this._currentUser = currentUser; })
    }

    /// Gets all Plants, ProductLines, Options, and AdminOptions for a given Retailer
    getOptionsForPlant(plantNumber: number): Observable<OptionsAdmin> {
        return this._http.get(`${this.baseUrl}/api/options/options-admin/${plantNumber}`)
            .map(x => {
                this._optionsAdminCache = x.json();
                return this._optionsAdminCache;
            })
            .catch(this._http.handleError);
    }

    /// Gets all AdminCategories for a Plant and Model
    getOptionsAdminCategories(plantId: number, modelId: number): Observable<AdminOptionCategory[]> {
        return this._http.get(`${this.baseUrl}/api/options/options-admin/categories/${plantId}/${modelId}`)
            .map(x => {
                //this._optionsAdminCatsCache = x.json();
                return x.json();
            })
            .catch(this._http.handleError);
	}

	/// Gets iSeries Categories for Model
	getCategoriesByModel(modelId: number): Observable<Category[]> {
		return this._http.get(`${this.baseUrl}/api/categories/${modelId}`)
			.map(x => {
				return x.json();
			})
			.catch(this._http.handleError);
	}

    /// Inserts/Updates a new/existing AdminOption
    insUpdAdminOption(adminOption: AdminOption[], userId: string): Observable<OptionsAdmin> {
        return this._http.put(`${this.baseUrl}/api/options/options-admin/create/${userId}/${this.plantId$.value}`, JSON.stringify(adminOption))
            .map(x => {
                this._optionsAdminCache = x.json();
                return this._optionsAdminCache;
            })
            .catch(this._http.handleError);
    }

    /// Gets all Options for a Plant
    getOptionsByPlant(plantNumber: number): Option[] {
        let options = new Array<Option>();
        if (this.getOptionsCache !== undefined && this.getOptionsCache !== null) {
            this.getOptionsCache.filter(x => x.PlantNumber === plantNumber).forEach((op) => {
                if (options !== null && options.length > 0) {
                    if (!options.find(x => x.OptionNumber === op.OptionNumber)) {
                        options.push(op);
                    }
                } else {
                    options.push(op);
                }
            });
        }
        return options;
    }

    /// Gets all Options for a ProductLine/Category combo
    getFilterOptions(productLineDesc: string, homeCategoryDesc: string): Option[] {
        let filteredOptions = this.getOptionsCache;
        if (filteredOptions !== undefined && filteredOptions !== null && filteredOptions.length > 0) {
            if ((productLineDesc !== '' && !productLineDesc.toUpperCase().includes('ALL PRODUCT'))) {
                if (productLineDesc.indexOf('--') >= 0) {
                    let productLineCode = productLineDesc.split('--')[1];
                    productLineDesc = productLineDesc.split('--')[0];

                    filteredOptions = filteredOptions.filter(x => x.ProductLines !== undefined && x.ProductLines !== null && x.ProductLines.length > 0
                        && (x.ProductLines.filter(j => j.ProductLineDescription.trim().toUpperCase() === productLineDesc.trim().toUpperCase()
                            && j.ProductLineCode.toUpperCase() === productLineCode.toUpperCase()).length > 0));
                } else {
                    filteredOptions = filteredOptions.filter(x => x.ProductLines !== undefined && x.ProductLines !== null && x.ProductLines.length > 0
                        && (x.ProductLines.filter(j => j.ProductLineDescription.trim().toUpperCase() === productLineDesc.trim().toUpperCase()).length > 0));
                }
            }
            if ((homeCategoryDesc !== '' && !homeCategoryDesc.toUpperCase().includes('ALL CATEGORIES'))) {
                filteredOptions = filteredOptions.filter(x => x.CategoryDesc.trim().toUpperCase() === homeCategoryDesc.trim().toUpperCase());
            }
        }
        return filteredOptions;
    }

    /// Gets all Options for a ProductLine/Category combo
    getFilteredOptionsWithPLCode(productLineDesc: string, productLineCode: string, homeCategoryDesc: string): Option[] {
        let filteredOptions = this.getOptionsCache;
        if (filteredOptions !== undefined && filteredOptions !== null && filteredOptions.length > 0) {
            if ((productLineDesc !== '' && !productLineDesc.toUpperCase().includes('ALL PRODUCT'))) {
                filteredOptions = filteredOptions.filter(x => x.ProductLines !== undefined && x.ProductLines !== null && x.ProductLines.length > 0
                    && (x.ProductLines.filter(j => j.ProductLineDescription.toUpperCase() === productLineDesc.toUpperCase() && j.ProductLineCode.toUpperCase() === productLineCode.toUpperCase()).length > 0));
            }
            if ((homeCategoryDesc !== '' && !homeCategoryDesc.toUpperCase().includes('ALL CATEGORIES'))) {
                filteredOptions = filteredOptions.filter(x => x.CategoryDesc.toUpperCase() === homeCategoryDesc.toUpperCase());
            }
        }
        return filteredOptions;
    }

    /// Gets all Options for a ProductLine/Category combo
    getFilteredOptionsWithPLID(productLineId: number, homeCategoryDesc: string): Option[] {
        let filteredOptions = this.getOptionsCache;
        if (filteredOptions !== undefined && filteredOptions !== null && filteredOptions.length > 0) {
            filteredOptions = filteredOptions.filter(x => x.ProductLines !== undefined && x.ProductLines !== null && x.ProductLines.length > 0
                && (x.ProductLines.filter(j => Number(j.ProductLineId) === Number(productLineId)).length > 0));

            if ((homeCategoryDesc !== '' && !homeCategoryDesc.toUpperCase().includes('ALL CATEGORIES'))) {
                filteredOptions = filteredOptions.filter(x => x.CategoryDesc.toUpperCase() === homeCategoryDesc.toUpperCase());
            }
        }
        return filteredOptions;
    }

    /// Opens the new Options-Aside
    openAside(item: Option, options: Option[], userId:string, plantNumber: number, pageNumber: number = undefined, productLineId: number = 0, productLineCode: string = undefined, categoryId: number = undefined, searchString: string = undefined) {
        let optionsAdminAsidePageOptions = new OptionsAdminAsidePage();
        optionsAdminAsidePageOptions.option = item;
        optionsAdminAsidePageOptions.options = options;

        optionsAdminAsidePageOptions.plantNumber = plantNumber;
        optionsAdminAsidePageOptions.pageNumber = (pageNumber === undefined || pageNumber === null) ? 1 : pageNumber;
        optionsAdminAsidePageOptions.productLineId = productLineId;
        optionsAdminAsidePageOptions.productLineCode = (productLineCode === undefined || productLineCode === null) ? undefined : productLineCode;
        optionsAdminAsidePageOptions.categoryId = categoryId;
        optionsAdminAsidePageOptions.searchString = searchString;

        if (item.ProductLines !== undefined && item.ProductLines !== null && item.ProductLines.length > 0) {
            if (item.ProductLines.filter(pl => pl.ProductLineId === Number(productLineId)).length > 0) {
                optionsAdminAsidePageOptions.productLineCode = item.ProductLines.filter(pl => pl.ProductLineId === Number(productLineId))[0].ProductLineCode;
            }
        }

        this.showAdminAside$.next(true);
        this.optionsadminasidePageOptions$.next(optionsAdminAsidePageOptions);
        if (userId !== '') { this.userId$.next(userId); }
    }

    openBulkCategoryChangeAside(plantId: number, selectedOptions: Option[] = null) {
        this.bulkchangeOptions$.next(selectedOptions);
        this.showBulkCategoryChangeAside$.next(true);
    }

    /// Gets all Options that are in the cache
    get getOptionsCache(): Option[] {
        if (this._optionsAdminCache !== undefined && this._optionsAdminCache !== null
            && this._optionsAdminCache.Options !== undefined && this._optionsAdminCache.Options !== null) {
            return this._optionsAdminCache.Options;
        }
        return new Array<Option>();
    }

    /// Gets all Category that are in the cache
    get getHomeCategoriesCache(): Category[] {
        if (this._optionsAdminCache !== undefined && this._optionsAdminCache !== null
            && this._optionsAdminCache.Categories !== undefined && this._optionsAdminCache.Categories !== null) {
            return this._optionsAdminCache.Categories;
        }
        return new Array<Category>();
    }

    /// Gets all CustomerCategories that are in the cache
    get getCustomerCategoriesCache(): AdminOptionCategory[] {
        if (this._optionsAdminCache !== undefined && this._optionsAdminCache !== null
            && this._optionsAdminCache.CustomerCategories !== undefined && this._optionsAdminCache.CustomerCategories !== null) {
            return this._optionsAdminCache.CustomerCategories;
        }
        return new Array<AdminOptionCategory>();
    }

    setOptionsCache(options: Option[]) {
        if (this._optionsAdminCache !== undefined && this._optionsAdminCache !== null) {
            this._optionsAdminCache.Options = options;
        }
    }

    /// Gets all Options for a Plant
    getOptionsByPlantWithPaging(plantNumber: number, pageRequested: number = 1, optionsPerPage: number = 20, productLineCode: string = null, categoryId: number = 0, searchString: string = null, showUntouched: boolean = false): Observable<OptionsAdmin> {
        let aoSearch = new OptionsAdminSearch();
        aoSearch.plantNumber = plantNumber;
        aoSearch.pageRequested = pageRequested;
        aoSearch.optionsPerPage = optionsPerPage;
        aoSearch.productLineCode = productLineCode;
        aoSearch.categoryId = categoryId;
        aoSearch.searchString = searchString;
        aoSearch.showUntouched = showUntouched;

        return this._http.put(`${this.baseUrl}/api/options/options-admin/search`, JSON.stringify(aoSearch))
            .map(x => {
                this._optionsAdminCache = x.json();
                return this._optionsAdminCache;
            })
            .catch(this._http.handleError);
    }

    /// Gets all Options for a Plant
    bulkCategoryChange(plantNumber: number, homeCategoryId:number, customerCategoryId:number): Observable<boolean> {
        return this._http.get(`${this.baseUrl}/api/options/options-admin/${plantNumber}/${homeCategoryId}/${customerCategoryId}/bulkcategorychange`)
            .map(x => {
                return x.json();
            })
            .catch(this._http.handleError);
    }

}




// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/services/options-admin.service.ts